import type { ContractCategory } from "../services/contract-categories";
import type { ContractType, ContractCompany } from "../services/contract-types";

const CACHE_VERSION = "v1";
// Bump only worker signature cache to invalidate stale/broken entries without
// touching the other cached data (categories, types, etc.).
const WORKER_SIGNATURE_CACHE_VERSION = "v2";
const CACHE_DURATION_MS = 10 * 60 * 60 * 1000; // 10 hours

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface CachedContractType extends ContractType {
  company_details: ContractCompany;
}

export interface PreferredContractFilters {
  categories: string[];
  companies: string[];
}

// Worker signature data interface
export interface WorkerSignatureData {
  full_name: string | null;
  organization: string | null;
  signature_base64: string | null;
}

// Cache keys
const CACHE_KEYS = {
  categories: `contractCategories_${CACHE_VERSION}`,
  companies: `contractCompanies_${CACHE_VERSION}`,
  contractTypes: (category: string) =>
    `contractTypes_${category}_${CACHE_VERSION}`,
  userPreference: (userId: string) => `preferredCategory_${userId}`,
  userFilterPreference: (userId: string) =>
    `preferredContractFilters_${userId}`,
  workerSignature: (userId: string) =>
    `workerSignature_${userId}_${WORKER_SIGNATURE_CACHE_VERSION}`,
};

// Generic cache functions
function setCacheEntry<T>(key: string, data: T): void {
  const entry: CacheEntry<T> = {
    data,
    timestamp: Date.now(),
  };
  try {
    localStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
    console.warn("Failed to cache data:", error);
  }
}

function getCacheEntry<T>(key: string): T | null {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const entry: CacheEntry<T> = JSON.parse(stored);
    const isExpired = Date.now() - entry.timestamp > CACHE_DURATION_MS;

    if (isExpired) {
      localStorage.removeItem(key);
      return null;
    }

    return entry.data;
  } catch (error) {
    console.warn("Failed to read cache:", error);
    localStorage.removeItem(key);
    return null;
  }
}

function isCacheValid(key: string): boolean {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return false;

    const entry: CacheEntry<any> = JSON.parse(stored);
    return Date.now() - entry.timestamp <= CACHE_DURATION_MS;
  } catch (error) {
    return false;
  }
}

function getAllStorageKeys(): string[] {
  const keys: string[] = [];
  try {
    if (
      typeof localStorage.length === "number" &&
      typeof localStorage.key === "function"
    ) {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) keys.push(key);
      }
      return keys;
    }
    return Object.keys(localStorage);
  } catch (error) {
    return [];
  }
}

// Contract Categories Cache
export function getCachedCategories(): ContractCategory[] | null {
  return getCacheEntry<ContractCategory[]>(CACHE_KEYS.categories);
}

export function setCachedCategories(categories: ContractCategory[]): void {
  setCacheEntry(CACHE_KEYS.categories, categories);
}

export function isCategoriesCacheValid(): boolean {
  return isCacheValid(CACHE_KEYS.categories);
}

// Contract Companies Cache
export function getCachedCompanies(): ContractCompany[] | null {
  return getCacheEntry<ContractCompany[]>(CACHE_KEYS.companies);
}

export function setCachedCompanies(companies: ContractCompany[]): void {
  setCacheEntry(CACHE_KEYS.companies, companies);
}

export function isCompaniesCacheValid(): boolean {
  return isCacheValid(CACHE_KEYS.companies);
}

// Contract Types Cache (per category)
export function getCachedContractTypes(
  category: string
): CachedContractType[] | null {
  return getCacheEntry<CachedContractType[]>(
    CACHE_KEYS.contractTypes(category)
  );
}

export function setCachedContractTypes(
  category: string,
  contractTypes: CachedContractType[]
): void {
  setCacheEntry(CACHE_KEYS.contractTypes(category), contractTypes);
}

export function isContractTypesCacheValid(category: string): boolean {
  return isCacheValid(CACHE_KEYS.contractTypes(category));
}

// User Preference Cache
export function getUserPreferredCategory(userId: string): string | null {
  try {
    return localStorage.getItem(CACHE_KEYS.userPreference(userId));
  } catch (error) {
    console.warn("Failed to read user preference:", error);
    return null;
  }
}

export function setUserPreferredCategory(
  userId: string,
  category: string
): void {
  try {
    localStorage.setItem(CACHE_KEYS.userPreference(userId), category);
  } catch (error) {
    console.warn("Failed to save user preference:", error);
  }
}

const normalizePreferredContractFilters = (
  value: unknown
): PreferredContractFilters | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const record = value as Partial<PreferredContractFilters>;
  const categories = Array.isArray(record.categories)
    ? record.categories.filter(
        (category): category is string => typeof category === "string"
      )
    : [];
  const companies = Array.isArray(record.companies)
    ? record.companies.filter(
        (company): company is string => typeof company === "string"
      )
    : [];

  return { categories, companies };
};

export function getUserPreferredContractFilters(
  userId: string
): PreferredContractFilters | null {
  try {
    const stored = localStorage.getItem(
      CACHE_KEYS.userFilterPreference(userId)
    );
    if (!stored) return null;

    return normalizePreferredContractFilters(JSON.parse(stored));
  } catch (error) {
    console.warn("Failed to read user filter preference:", error);
    localStorage.removeItem(CACHE_KEYS.userFilterPreference(userId));
    return null;
  }
}

export function setUserPreferredContractFilters(
  userId: string,
  filters: PreferredContractFilters
): void {
  try {
    localStorage.setItem(
      CACHE_KEYS.userFilterPreference(userId),
      JSON.stringify({
        categories: filters.categories,
        companies: filters.companies,
      })
    );
  } catch (error) {
    console.warn("Failed to save user filter preference:", error);
  }
}

// Worker Signature Cache
export function getCachedWorkerSignature(
  userId: string
): WorkerSignatureData | null {
  return getCacheEntry<WorkerSignatureData>(CACHE_KEYS.workerSignature(userId));
}

export function setCachedWorkerSignature(
  userId: string,
  data: WorkerSignatureData
): void {
  setCacheEntry(CACHE_KEYS.workerSignature(userId), data);
}

export function clearWorkerSignatureCache(userId: string): void {
  try {
    localStorage.removeItem(CACHE_KEYS.workerSignature(userId));
  } catch (error) {
    console.warn("Failed to clear worker signature cache:", error);
  }
}

// Clear only contract metadata cache (categories, companies, contract types)
export function clearContractMetadataCache(): void {
  const prefixes = [
    "contractCategories_",
    "contractCompanies_",
    "contractTypes_",
  ];

  try {
    const keys = getAllStorageKeys();
    keys.forEach((key) => {
      if (prefixes.some((prefix) => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn("Failed to clear contract metadata cache:", error);
  }
}

// Clear all cache (for testing or manual refresh)
export function clearContractCache(): void {
  const keys = getAllStorageKeys();
  keys.forEach((key) => {
    if (
      key.startsWith("contract") ||
      key.startsWith("preferredCategory_") ||
      key.startsWith("preferredContractFilters_") ||
      key.startsWith("workerSignature_")
    ) {
      localStorage.removeItem(key);
    }
  });
}
